import { HugeiconsIcon } from "@hugeicons/react";
import { Briefcase01Icon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { MetricTiles } from "@/components/dashboard/metric-tiles";
import { ChartCard } from "@/components/dashboard/chart-card";
import { TrendArea } from "@/components/dashboard/charts";
import { BreakdownTable, type Column } from "@/components/dashboard/breakdown-table";
import { platformLabelCol, regionLabelCol, spendCol, impressionsCol, clicksCol, ctrCol } from "@/components/dashboard/columns";
import { Skeleton } from "@/components/ui/skeleton";
import { FadeIn } from "@/components/ui/motion";
import { seriesColor } from "@/lib/chart-theme";
import { useTotals, useSeries, useBreakdown } from "@/lib/use-metrics";
import { useReference } from "@/lib/use-reference";
import { useMetricMoney } from "@/lib/use-money";
import { compact } from "@/lib/format";
import type { BreakdownRow } from "@/lib/api/metrics";

export function Jobs() {
  const { metrics, platforms } = useReference();
  const m = useMetricMoney();
  const totals = useTotals("job_ads");
  const series = useSeries("job_ads");
  const byPlatform = useBreakdown("platform", "job_ads");
  const byRegion = useBreakdown("region", "job_ads");

  const loading = totals.isPending;
  // Teamtailor is a tool, not a media channel, but it is where applications
  // come from — so it stays in this table next to the ad channels that ran jobs.
  const platformRows = byPlatform.data ?? [];

  const appsCol: Column<BreakdownRow> = { key: "applications", header: "Sollicitaties", align: "right", render: (r) => compact(r.applications), exportValue: (r) => r.applications ?? "" };
  const cpaCol: Column<BreakdownRow> = { key: "cpa", header: "Kost/soll.", align: "right", render: (r) => m.fmt(r.cost_per_application), exportValue: (r) => r.cost_per_application != null ? Math.round(r.cost_per_application * 100) / 100 : "" };

  const columns: Column<BreakdownRow>[] = [
    platformLabelCol("Platform", platforms), spendCol(m.fmt), impressionsCol(), clicksCol(), ctrCol(), appsCol, cpaCol,
  ];

  const regionColumns: Column<BreakdownRow>[] = [
    regionLabelCol("Regio"), spendCol(m.fmt), appsCol, cpaCol,
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Job ads"
        icon={Briefcase01Icon}
        title="Recruitment-advertenties"
        description="Wervingscampagnes — uitgaven, sollicitaties en kost per sollicitatie."
        tabs={<GlobalFilterBar showActivity={false} activity="job_ads" />}
      />

      <FadeIn delay={0}>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
          </div>
        ) : (
          <MetricTiles
            keys={["spend", "impressions", "clicks", "ctr", "applications", "cost_per_application"]}
            values={totals.data!.kpis as unknown as Record<string, number | null>}
            prev={totals.data!.prev as unknown as Record<string, number | null>}
            metrics={metrics}
            columns={3}
            money={m.fmt}
          />
        )}
      </FadeIn>

      <FadeIn delay={0.05}>
        <div className="grid gap-4 lg:grid-cols-3">
          <ChartCard title="Sollicitaties & uitgaven" subtitle="Dagelijks" bodyClassName="h-80" className="lg:col-span-2">
            <TrendArea
              data={series.data ?? []}
              series={[
                { dataKey: "applications", name: "Sollicitaties", color: seriesColor("applications"), axis: "left" },
                { dataKey: "spend", name: "Uitgaven", color: seriesColor("spend"), kind: "currency", axis: "right" },
              ]}
            />
          </ChartCard>
          <BreakdownTable title="Per regio" description="Sollicitaties en kost per sollicitatie per markt." rows={byRegion.data ?? []} columns={regionColumns} rowKey={(r) => r.key} empty="Geen sollicitaties in deze periode." exportName="Prado Pulse — Job ads per regio" />
        </div>
      </FadeIn>

      <FadeIn delay={0.1}>
        <BreakdownTable title="Per channel" description="Sollicitaties, uitgaven en kost per sollicitatie per media-channel over de gekozen periode." rows={platformRows} columns={columns} rowKey={(r) => r.key} empty="Geen sollicitaties of wervingsadvertenties in deze periode." exportName="Prado Pulse — Job ads per channel" />
      </FadeIn>
    </div>
  );
}
